/**
 * Lightweight analytics helper.
 *
 * Lovable's hosted analytics is Plausible-based and exposes `window.plausible`
 * on the published site. This wrapper safely forwards custom events to it
 * (and to Google Tag Manager's dataLayer if present), and is a no-op in
 * environments where neither exists. Events are also mirrored to
 * `console.debug` so you can verify firing locally.
 */

type AnalyticsProps = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    plausible?: (event: string, options?: { props?: AnalyticsProps; callback?: () => void }) => void;
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const sanitize = (props?: AnalyticsProps): AnalyticsProps | undefined => {
  if (!props) return undefined;
  const out: AnalyticsProps = {};
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined || v === null) continue;
    out[k] = typeof v === "string" && v.length > 120 ? v.slice(0, 120) : v;
  }
  return out;
};

export function trackEvent(name: string, props?: AnalyticsProps) {
  if (typeof window === "undefined") return;
  const cleaned = sanitize(props);
  try {
    window.plausible?.(name, cleaned ? { props: cleaned } : undefined);
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...cleaned });
    }
    // eslint-disable-next-line no-console
    console.debug("[analytics]", name, cleaned ?? "");
  } catch (err) {
    // never let analytics break the app
    // eslint-disable-next-line no-console
    console.warn("[analytics] failed", err);
  }
}

export function trackPageview(path: string) {
  trackEvent("pageview", { path });
}